import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Modal as DialogModal, Button as DialogButton } from "./_shared";
import { ClipboardCheck } from "lucide-react";
import { usePermission } from "../../hooks/usePermission";
import { validateLocationForm } from "../../components/admin/locations/validation";
import {
  MapPin,
  Navigation,
  FileText,
  Image,
} from "lucide-react";
import {
  API,
  apiFetch,
  useToast,
  getAdminSessionToken,
  friendlyHttpMessage,
  friendlyNetworkMessage,
} from "./_shared";
import { resolveAssetDisplayUrl } from "../../lib/assetUrl";
import { MapContainer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import OpenFreeMapLayer from "../../components/OpenFreeMapLayer";
import LocationEditorModal from "../../components/admin/LocationEditorModal";
import LocationList from "../../components/admin/locations/LocationList";
import { useAdminJobs } from "../../contexts/AdminJobsContext";
import { multipartUploadToS3, buildUniqueUploadKey, SINGLE_UPLOAD_MAX_BYTES, TOO_LARGE_SINGLE_UPLOAD_MESSAGE } from "../../lib/multipartUpload";

/* ────────────────────────────────────────────────────── */
/* Constants */
/* ────────────────────────────────────────────────────── */
const DALAT_CENTER = [11.9404, 108.4583];
// S3 base prefix: locations/${slug}/
// No draft folder – all files stay local (blob) until Save.

const TABS = [
  { id: "basic", label: "Thông tin", icon: FileText },
  { id: "media", label: "Media", icon: Image },
  { id: "location", label: "Vị trí", icon: Navigation },
  { id: "review", label: "Kiểm tra", icon: ClipboardCheck },
];

const makeIdempotencyKey = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    // ignore
  }
  return `idem_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const jitter = (ms) => {
  const n = Number(ms) || 0;
  if (n <= 0) return 0;
  const spread = n * 0.2;
  return Math.max(0, Math.round(n - spread + Math.random() * spread * 2));
};

const isRetryableStatus = (status) =>
  [408, 425, 429, 500, 502, 503, 504, 520, 522, 524].includes(Number(status));

/** BE: cùng X-Idempotency-Key mà yêu cầu trước còn đang chạy → 409 IDEMPOTENCY_IN_PROGRESS. */
const isAddInProgressConflict = (err) => Number(err?.status) === 409 && err?.code === "IDEMPOTENCY_IN_PROGRESS";

/** Parse lat/lng; iOS/VN locale may use "11,9404" instead of "11.9404". */
function parseCoordinateInput(v) {
  if (v == null || v === "") return NaN;
  if (typeof v === "number") return Number.isFinite(v) ? v : NaN;
  let s = String(v).trim().replace(/\s/g, "");
  if (/^-?\d+,\d+$/.test(s)) s = s.replace(",", ".");
  else s = s.replace(/,/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

// Single POST /api/media/upload bị BE giới hạn ~4.5MB (413 FILE_TOO_LARGE); lớn hơn → multipart presigned PUT lên S3.
const DIRECT_UPLOAD_MAX_BYTES = SINGLE_UPLOAD_MAX_BYTES;

const LOCAL_DRAFT_PREFIX = "adminLocationDraft:v1:";
const writeLocalDraft = (id, formLike) => {
  try {
    localStorage.setItem(`${LOCAL_DRAFT_PREFIX}${id}`, JSON.stringify({ savedAt: Date.now(), form: formLike }));
  } catch {
    /* hết dung lượng / chế độ riêng tư */
  }
};
const removeLocalDraft = (id) => {
  try {
    localStorage.removeItem(`${LOCAL_DRAFT_PREFIX}${id}`);
  } catch {
    /* ignore */
  }
};

function createEmptyForm() {
  return {
    name: "",
    slug: "",
    category: "",
    categorySlug: "",
    ward: "",
    district: "",
    city: "Đà Lạt",
    phone: "",
    shortIntro: "",
    description: "",
    tiktokEmbed: "",
    tiktokUrl: "",
    price: 0,
    priceMin: 0,
    priceMax: 0,
    priceRange: "0",
    openTime: "",
    closeTime: "",
    latitude: DALAT_CENTER[0],
    longitude: DALAT_CENTER[1],
    mapIcon: "all",
    mapIconAsset: { key: "", url: "", mimeType: "", size: 0, alt: "" },
    status: "draft",
    seoTitle: "",
    seoDescription: "",
    seoKeywords: "",
    highlights: "",
    tips: "",
    faq: "",
    geoapifyPlaceId: "",
    mediaBlocks: [],
    // Khối nội dung UI chưa hỗ trợ sửa (video/văn bản...) — giữ nguyên khi lưu: [{ index, block }]
    preservedMediaBlocks: [],
    heroGallery: [],
    homeLinkUrl: "",
    homeLinkLabel: "",
    homeLinkClicks: 0,
  };
}

function createEmptyDraftMedia() {
  return {
    cover: null,
    gallery: [],
    videos: [],
  };
}

function hasLocalFileInAsset(a) {
  return Boolean(a && typeof a === "object" && a.__localFile instanceof File);
}

function draftMediaHasContent(d) {
  if (!d) return false;
  const c = d.cover;
  if (c && (c.key || c.url || hasLocalFileInAsset(c))) return true;
  if (Array.isArray(d.gallery) && d.gallery.length > 0) return true;
  if (Array.isArray(d.videos) && d.videos.length > 0) return true;
  return false;
}

function mediaBlockHasUserInput(b) {
  if (!b || typeof b !== "object") return false;
  if (String(b.caption || "").trim()) return true;
  if (String(b.title || "").trim()) return true;
  if (String(b.content || "").trim()) return true;
  if (String(b.videoUrl || "").trim()) return true;
  const img = b.image;
  if (img && (img.key || img.url || hasLocalFileInAsset(img))) return true;
  const th = b.thumbnail;
  if (th && (th.key || th.url || hasLocalFileInAsset(th))) return true;
  const vid = b.video;
  if (vid && (vid.key || vid.url || hasLocalFileInAsset(vid))) return true;
  return false;
}

/** True = có thể reset form thêm mới an toàn (không mất nháp đang nhập). */
function isPristineAddFormState(formLike, draft, videoDraftLike) {
  const f = formLike || {};
  const vd = videoDraftLike || createEmptyVideoDraft();
  if (String(vd.titles || "").trim()) return false;
  if (Array.isArray(vd.videos) && vd.videos.length > 0) return false;
  if (Array.isArray(vd.thumbs) && vd.thumbs.length > 0) return false;
  if (String(f.name || "").trim()) return false;
  if (String(f.slug || "").trim()) return false;
  if (String(f.category || "").trim()) return false;
  if (String(f.categorySlug || "").trim()) return false;
  if (String(f.ward || "").trim()) return false;
  if (String(f.district || "").trim()) return false;
  if (String(f.city || "").trim() && String(f.city).trim() !== "Đà Lạt") return false;
  if (String(f.phone || "").trim()) return false;
  if (String(f.shortIntro || "").trim()) return false;
  if (String(f.description || "").trim()) return false;
  if (String(f.tiktokEmbed || "").trim()) return false;
  if (String(f.tiktokUrl || "").trim()) return false;
  if (Number(f.price || 0) !== 0) return false;
  if (String(f.openTime || "").trim()) return false;
  if (String(f.closeTime || "").trim()) return false;
  const lat = Number(f.latitude);
  const lng = Number(f.longitude);
  if (Number.isFinite(lat) && Math.abs(lat - DALAT_CENTER[0]) > 1e-5) return false;
  if (Number.isFinite(lng) && Math.abs(lng - DALAT_CENTER[1]) > 1e-5) return false;
  if (String(f.mapIcon || "all") !== "all") return false;
  const mia = f.mapIconAsset;
  if (mia && (mia.key || mia.url || hasLocalFileInAsset(mia))) return false;
  if (f.status && f.status !== "draft") return false;
  if (String(f.seoTitle || "").trim()) return false;
  if (String(f.seoDescription || "").trim()) return false;
  if (String(f.seoKeywords || "").trim()) return false;
  if (String(f.highlights || "").trim()) return false;
  if (String(f.tips || "").trim()) return false;
  if (String(f.faq || "").trim()) return false;
  if (String(f.geoapifyPlaceId || "").trim()) return false;
  if (String(f.homeLinkUrl || "").trim()) return false;
  if (String(f.homeLinkLabel || "").trim()) return false;
  if (Number(f.homeLinkClicks || 0) !== 0) return false;
  if (Array.isArray(f.heroGallery) && f.heroGallery.length > 0) return false;
  if (Array.isArray(f.mediaBlocks) && f.mediaBlocks.some(mediaBlockHasUserInput)) return false;
  if (draftMediaHasContent(draft)) return false;
  return true;
}

/**
 * Tách mediaBlocks từ server: khối ảnh → UI chỉnh sửa; khối khác (video/văn bản/không rõ) → giữ nguyên
 * kèm vị trí gốc để ghép lại khi lưu (không chuyển thành ảnh rỗng → tránh mất dữ liệu).
 */
function splitMediaBlocksForUi(rawBlocks) {
  const uiBlocks = [];
  const preserved = [];
  (Array.isArray(rawBlocks) ? rawBlocks : []).forEach((b, index) => {
    if (!b || typeof b !== "object") return;
    const type = String(b.type || "").toLowerCase();
    if (type === "image") {
      uiBlocks.push({
        type: "image",
        caption: b.caption || "",
        title: b.title || "",
        content: "",
        image: b.image ? normalizeAsset(b.image) : null,
      });
    } else {
      preserved.push({ index, block: b });
    }
  });
  return { uiBlocks, preserved };
}

/** Ghép lại khối được giữ nguyên vào đúng vị trí gốc (tương đối) giữa các khối ảnh đã sửa. */
function mergePreservedMediaBlocks(editedBlocks, preserved) {
  const out = Array.isArray(editedBlocks) ? [...editedBlocks] : [];
  const list = (Array.isArray(preserved) ? preserved : [])
    .filter((p) => p && p.block)
    .sort((a, b) => Number(a.index) - Number(b.index));
  for (const p of list) {
    const at = Math.max(0, Math.min(out.length, Number(p.index) || 0));
    out.splice(at, 0, p.block);
  }
  return out;
}

/** Địa điểm cũ chưa có mediaBlocks: chỉ dựng khối ảnh từ ảnh thư viện thật (không chép ảnh bìa / mô tả). */
function buildLegacyMediaBlocksFromGallery(gallery) {
  const out = [];
  for (const g of normalizeGallery(gallery)) {
    if (!g?.url) continue;
    out.push({ type: "image", caption: g.alt || "", title: "", content: "", image: g });
  }
  return out;
}

function createEmptyVideoDraft() {
  return {
    titles: "",
    videos: [],
    thumbs: [],
  };
}

/* ────────────────────────────────────────────────────── */
/* Leaflet Map Picker */
/* ────────────────────────────────────────────────────── */
const pickerIcon = L.divIcon({
  html: `<div style="width:28px;height:28px;background:#4F6F52;border:3px solid rgba(250,249,246,0.95);border-radius:50%;box-shadow:0 8px 22px rgba(58,90,64,0.28);display:flex;align-items:center;justify-content:center;">
    <div style="width:6px;height:6px;background:white;border-radius:50%;"></div>
  </div>`,
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const PickerEvents = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const MapSync = ({ lat, lng }) => {
  const map = useMap();
  const prev = React.useRef(null);

  useEffect(() => {
    const key = `${lat},${lng}`;
    if (prev.current !== key) {
      prev.current = key;
      map.panTo([lat, lng], { animate: true, duration: 0.5 });
    }
  }, [lat, lng, map]);

  return null;
};

const MapPicker = ({ lat, lng, onPick }) => {
  const latN = parseCoordinateInput(lat);
  const lngN = parseCoordinateInput(lng);
  const safeLat = Number.isFinite(latN) ? latN : DALAT_CENTER[0];
  const safeLng = Number.isFinite(lngN) ? lngN : DALAT_CENTER[1];

  return (
    <div className="overflow-hidden rounded-xl border border-admin-border bg-admin-surface shadow-sm" style={{ height: 340 }}>
      <MapContainer
        center={[safeLat, safeLng]}
        zoom={15}
        scrollWheelZoom
        attributionControl={false}
        style={{ height: "100%", width: "100%" }}
        zoomControl
      >
        {/* Admin chọn vị trí: giữ POI của nền để dễ định vị */}
        <OpenFreeMapLayer hidePois={false} />
        <MapSync lat={safeLat} lng={safeLng} />
        <PickerEvents onPick={onPick} />
        <Marker
          position={[safeLat, safeLng]}
          icon={pickerIcon}
          draggable
          eventHandlers={{
            dragend(e) {
              const p = e.target.getLatLng();
              onPick(p.lat, p.lng);
            },
          }}
        />
      </MapContainer>
    </div>
  );
};

/* ────────────────────────────────────────────────────── */
/* Helpers */
/* ────────────────────────────────────────────────────── */
async function createVideoThumbFile(videoFile, { atSeconds = 0.6 } = {}) {
  if (!(videoFile instanceof File)) throw new Error("Missing video file");
  const objectUrl = URL.createObjectURL(videoFile);

  const cleanup = () => {
    try { URL.revokeObjectURL(objectUrl); } catch { /* ignore */ }
  };

  try {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = "anonymous";
    video.src = objectUrl;

    await new Promise((resolve, reject) => {
      const onErr = () => reject(new Error("Không thể đọc video để tạo thumbnail"));
      video.addEventListener("error", onErr, { once: true });
      video.addEventListener("loadedmetadata", () => resolve(), { once: true });
    });

    const duration = Number(video.duration || 0);
    const seekTo = Number.isFinite(duration) && duration > 0
      ? Math.min(Math.max(0, atSeconds), Math.max(0, duration - 0.2))
      : 0;

    await new Promise((resolve, reject) => {
      const onErr = () => reject(new Error("Không thể seek video để tạo thumbnail"));
      video.addEventListener("error", onErr, { once: true });
      video.addEventListener("seeked", () => resolve(), { once: true });
      try { video.currentTime = seekTo; } catch { reject(new Error("Không thể seek video")); }
    });

    const vw = Math.max(1, Number(video.videoWidth || 0));
    const vh = Math.max(1, Number(video.videoHeight || 0));
    const maxW = 960;
    const scale = vw > maxW ? maxW / vw : 1;
    const w = Math.max(1, Math.round(vw * scale));
    const h = Math.max(1, Math.round(vh * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Không thể tạo canvas");
    ctx.drawImage(video, 0, 0, w, h);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
    if (!blob) throw new Error("Không thể tạo ảnh thumbnail");

    const baseName = String(videoFile.name || "video").replace(/\.[^.]+$/, "") || "video";
    return new File([blob], `${baseName}-thumb.jpg`, { type: "image/jpeg" });
  } finally {
    cleanup();
  }
}

function fileNameFromKey(key = "") {
  const s = String(key || "");
  const parts = s.split("/");
  return parts[parts.length - 1] || "file";
}


function safeFolderSlug(value = "", fallback = "location") {
  const out = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return out || fallback;
}

function round6(value) {
  return Math.round(Number(value) * 1e6) / 1e6;
}

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function formatMoneyInput(value = "") {
  const digits = onlyDigits(value);
  if (!digits) return "";
  return Number(digits).toLocaleString("vi-VN");
}

function parsePriceRange(value = "") {
  const [minRaw = "", maxRaw = ""] = String(value || "").split("-");

  return {
    priceMin: Number(onlyDigits(minRaw)) || 0,
    priceMax: Number(onlyDigits(maxRaw)) || 0,
  };
}

function formatPriceRange(min, max) {
  const a = Number(min || 0);
  const b = Number(max || 0);

  if (!a && !b) return "0";
  if (a && b) return `${a.toLocaleString("vi-VN")} - ${b.toLocaleString("vi-VN")}`;

  return `${(a || b).toLocaleString("vi-VN")}`;
}

function normalizeAsset(asset) {
  if (!asset) return null;
  if (typeof asset === "string") {
    return { key: "", url: asset, mimeType: "", size: 0, alt: "" };
  }
  return {
    key: asset.key || "",
    url: asset.url || "",
    mimeType: asset.mimeType || "",
    size: asset.size || 0,
    alt: asset.alt || "",
    ...asset,
  };
}

function normalizeGallery(gallery) {
  if (!Array.isArray(gallery)) return [];
  return gallery.map((g) => normalizeAsset(g)).filter(Boolean);
}

function normalizeVideos(videos) {
  if (!Array.isArray(videos)) return [];
  return videos
    .map((v) => {
      if (!v) return null;
      if (typeof v === "string") {
        return {
          key: "",
          url: v,
          mimeType: "",
          size: 0,
          title: "",
          thumbnail: {},
        };
      }
      return {
        key: v.key || "",
        url: v.url || "",
        mimeType: v.mimeType || "",
        size: v.size || 0,
        title: v.title || "",
        thumbnail: v.thumbnail ? normalizeAsset(v.thumbnail) : {},
        ...v,
      };
    })
    .filter(Boolean);
}

const set = (setForm) => (key) => (e) => {
  const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
  setForm((p) => ({ ...p, [key]: val }));
};

/* ────────────────────────────────────────────────────── */
/* Small UI */
/* ────────────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────── */
/* Main Component */
/* ────────────────────────────────────────────────────── */
const AdminLocations = () => {
  const { push: toast, ToastPortal } = useToast();
  const { beginJob, hasRunningJob } = useAdminJobs();

  const [categories, setCategories] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  // Tiến trình nền (thêm địa điểm) đọc qua ref: chỉ mở lại form khi không có form nào đang mở
  const modalOpenRef = useRef(false);
  useEffect(() => {
    modalOpenRef.current = modalOpen;
  }, [modalOpen]);
  // Khoá idempotency giữ lại khi lần thêm trước có kết quả chưa rõ → gửi lại không tạo trùng
  const addIdemKeyRef = useRef(null);
  const [modalMode, setModalMode] = useState("add");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState("");
  // Lỗi kiểm tra dữ liệu theo từng ô (từ backend 422) — hiện ngay dưới ô nhập
  const [formErrors, setFormErrors] = useState({});
  const clearFormError = (key) => setFormErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  const { can } = usePermission();

  // ── Chưa lưu thay đổi · tự lưu nháp cục bộ · cảnh báo khi thoát (F02) ──
  const sig = (f, d) =>
    JSON.stringify([
      f,
      d?.cover?.url || d?.cover?.__localFile?.name || "",
      (d?.gallery || []).map((g) => g.url || g.__localFile?.name || "").join("|"),
      (d?.videos || []).map((v) => v.url || v.__localFile?.name || "").join("|"),
    ]);
  const baselineRef = useRef("");
  const [guardOpen, setGuardOpen] = useState(false);
  const [lastAutosave, setLastAutosave] = useState(null);
  const [draftBanner, setDraftBanner] = useState(null);
  const draftKey = `adminLocationDraft:v1:${modalMode === "edit" && editId ? editId : "new"}`;
  const readDraft = () => {
    try {
      return JSON.parse(localStorage.getItem(draftKey) || "null");
    } catch {
      return null;
    }
  };
  const clearLocalDraft = useCallback(() => {
    try {
      localStorage.removeItem(draftKey);
    } catch {
      /* ignore */
    }
    setDraftBanner(null);
    setLastAutosave(null);
  }, [draftKey]);

  const [form, setForm] = useState(() => createEmptyForm());
  const [tab, setTab] = useState("location");

  const [draftMedia, setDraftMedia] = useState(() => createEmptyDraftMedia());
  const [uploading, setUploading] = useState({ cover: false, videos: false });
  const [videoDraft, setVideoDraft] = useState(() => createEmptyVideoDraft());

  const setter = set(setForm);

  const TAB_ORDER = useMemo(() => ["basic", "media", "location", "review"], []);
  const tabIndex = useMemo(() => Math.max(0, TAB_ORDER.indexOf(tab)), [TAB_ORDER, tab]);
  const isFirstStep = tabIndex === 0;
  const isLastStep = tabIndex === TAB_ORDER.length - 1;

  const goPrevStep = () => setTab(TAB_ORDER[Math.max(0, tabIndex - 1)]);
  const goNextStep = () => setTab(TAB_ORDER[Math.min(TAB_ORDER.length - 1, tabIndex + 1)]);

  const [reloadKey, setReloadKey] = useState(0);

  const fetch = useCallback(async () => {
    setReloadKey((k) => k + 1);
    try {
      const cats = await apiFetch(`${API}/categories`, { retry: { retries: 5, baseDelayMs: 650, maxDelayMs: 5500 } });
      setCategories((prev) => (Array.isArray(cats) ? cats : prev));
    } catch {
      /* giữ danh mục cũ nếu tải lỗi */
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // /admin/locations?new=1 (từ thanh lệnh Ctrl K) → mở sẵn form thêm mới rồi bỏ tham số khỏi URL
  const [urlParams, setUrlParams] = useSearchParams();
  const wantsNew = urlParams.get("new") === "1";

  const openAdd = () => {
    // Đang có tiến trình thêm chạy nền (nút Thêm đã bị khoá; chặn cả đường Ctrl K ?new=1)
    if (hasRunningJob) {
      toast("Đang có tiến trình thêm địa điểm — vui lòng đợi hoàn tất.", "info");
      return;
    }
    // Đang ở flow thêm + đã có nhập liệu: mở lại modal, giữ form/nháp (đóng nhầm / lỗi lưu).
    if (modalMode === "add" && !editId && !isPristineAddFormState(form, draftMedia, videoDraft)) {
      setModalOpen(true);
      return;
    }

    addIdemKeyRef.current = null;
    setForm(createEmptyForm());
    setModalMode("add");
    setEditId(null);
    setTab("basic");
    setDraftMedia(createEmptyDraftMedia());
    setVideoDraft(createEmptyVideoDraft());
    setModalOpen(true);
  };

  useEffect(() => {
    if (!wantsNew) return;
    openAdd();
    const next = new URLSearchParams(urlParams);
    next.delete("new");
    setUrlParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsNew]);

  const openEdit = (loc) => {
    const catId = String(loc.category?._id || loc.category || "");
    const rawBlocks = Array.isArray(loc.mediaBlocks) ? loc.mediaBlocks.filter((b) => b && typeof b === "object") : [];
    const { uiBlocks, preserved } = splitMediaBlocksForUi(rawBlocks);

    setForm({
      ...createEmptyForm(),
      name: loc.name || "",
      slug: loc.slug || "",
      category: catId,
      categorySlug: loc.categorySlug || "",
      ward: loc.ward || "",
      district: loc.district || "",
      city: loc.city || "Đà Lạt",
      phone: loc.phone || "",
      shortIntro: loc.shortIntro || "",
      description: loc.description || "",
      tiktokEmbed: loc.tiktokEmbed || "",
      tiktokUrl: loc.tiktokUrl || "",
      price: Number(loc.priceMin ?? loc.price ?? 0),
      priceMin: Number(loc.priceMin ?? loc.price ?? 0),
      priceMax: Number(loc.priceMax ?? 0),
      priceRange: formatPriceRange(
        loc.priceMin ?? loc.price ?? 0,
        loc.priceMax ?? 0
      ),
      openTime: loc.openTime || "",
      closeTime: loc.closeTime || "",
      latitude: loc.latitude ?? DALAT_CENTER[0],
      longitude: loc.longitude ?? DALAT_CENTER[1],
      mapIcon: loc.mapIcon || "all",
      mapIconAsset: loc.mapIconAsset || { key: "", url: "", mimeType: "", size: 0, alt: "" },
      status: loc.status || (loc.isActive === false ? "draft" : "published"),
      seoTitle: loc.seoTitle || "",
      seoDescription: loc.seoDescription || "",
      seoKeywords: Array.isArray(loc.seoKeywords) ? loc.seoKeywords.join(", ") : loc.seoKeywords || "",
      highlights: Array.isArray(loc.highlights) ? loc.highlights.join("\n") : "",
      tips: Array.isArray(loc.tips) ? loc.tips.join("\n") : "",
      faq: Array.isArray(loc.faq)
        ? loc.faq.map((x) => `${x.question || ""}|${x.answer || ""}`).join("\n")
        : "",
      geoapifyPlaceId: loc.geoapifyPlaceId || "",
      homeLinkUrl: loc.homeLinkUrl || "",
      homeLinkLabel: loc.homeLinkLabel || "",
      homeLinkClicks: Number(loc.homeLinkClicks || 0),
      mediaBlocks: rawBlocks.length > 0 ? uiBlocks : buildLegacyMediaBlocksFromGallery(loc.gallery),
      preservedMediaBlocks: preserved,
      heroGallery: Array.isArray(loc.heroGallery) ? loc.heroGallery : [],
    });

    setModalMode("edit");
    setEditId(loc._id);
    setTab("basic");
    setDraftMedia({
      cover: normalizeAsset(loc.image),
      gallery: normalizeGallery(loc.gallery),
      videos: normalizeVideos(loc.videos),
    });
    setVideoDraft(createEmptyVideoDraft());
    setModalOpen(true);
  };

  /** Mở form sửa từ hành động trong toast — không ghi đè form đang mở. */
  const openEditIfIdle = (loc) => {
    if (modalOpenRef.current) {
      toast("Hãy đóng cửa sổ chỉnh sửa đang mở trước.", "info");
      return;
    }
    if (loc?._id) openEdit(loc);
  };

  /**
   * Khôi phục dữ liệu của tiến trình thêm bị lỗi (snapshot lúc bấm Lưu, không phải form hiện tại).
   * Đã tạo được bản ghi → mở ở chế độ SỬA địa điểm đó (không POST lại). Trả false nếu đang mở form khác.
   */
  const restoreFailedAdd = ({ snapshot, media, createdId, idemKey }) => {
    if (modalOpenRef.current) {
      toast("Hãy đóng cửa sổ chỉnh sửa đang mở trước khi mở lại dữ liệu này.", "info");
      return false;
    }
    setFormErrors({});
    setForm(createdId ? { ...snapshot, status: "draft" } : snapshot);
    setDraftMedia(media || createEmptyDraftMedia());
    setVideoDraft(createEmptyVideoDraft());
    setModalMode(createdId ? "edit" : "add");
    setEditId(createdId || null);
    setTab("basic");
    addIdemKeyRef.current = createdId ? null : idemKey || null;
    setModalOpen(true);
    modalOpenRef.current = true;
    return true;
  };

  // Returns a local asset (blob URL + __localFile) — no S3 upload yet.
  // Used when LocationEditorModal picks a map-icon file.
  const storeLocalAsset = (file) => ({
    __localFile: file,
    url: URL.createObjectURL(file),
    mimeType: file.type || "",
    size: file.size || 0,
    key: "",
    alt: "",
  });

  const uploadOneToPrefix = async (file, prefix) => {
    const fd = new FormData();
    fd.append("file", file);

    const token = getAdminSessionToken();
    const url = `${API}/media/upload?prefix=${encodeURIComponent(String(prefix || ""))}`;
    let lastErr = null;

    for (let attempt = 0; attempt <= 5; attempt++) {
      try {
        const res = await window.fetch(url, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg =
            res.status === 413 || data?.code === "FILE_TOO_LARGE"
              ? TOO_LARGE_SINGLE_UPLOAD_MESSAGE
              : `Không upload được tệp "${file?.name || ""}". ${friendlyHttpMessage(res.status, data?.message)}`;
          const err = new Error(msg);
          err.status = res.status;
          err.code = data?.code;
          err.detail = `POST /api/media/upload → HTTP ${res.status}${data?.code ? ` (${data.code})` : ""} · ${file?.name || ""} (${Math.round((file?.size || 0) / 1024)}KB)`;
          const canRetry = attempt < 5 && isRetryableStatus(res.status);
          if (canRetry) {
            lastErr = err;
            await sleep(jitter(Math.min(12000, 700 * Math.pow(2, attempt))));
            continue;
          }
          throw err;
        }

        return data?.data || data;
      } catch (e) {
        const msg = String(e?.message || "");
        const isNetwork = e instanceof TypeError || /failed to fetch|network/i.test(msg);
        const canRetry = attempt < 5 && (isNetwork || isRetryableStatus(e?.status));
        if (canRetry) {
          lastErr = e;
          await sleep(jitter(Math.min(12000, 700 * Math.pow(2, attempt))));
          continue;
        }
        if (isNetwork && e?.status == null) {
          const err = new Error(`Không upload được tệp "${file?.name || ""}". ${friendlyNetworkMessage()}`);
          err.detail = `POST /api/media/upload: ${msg || "network error"} · ${file?.name || ""} (${Math.round((file?.size || 0) / 1024)}KB)`;
          err.uncertain = true;
          throw err;
        }
        throw e;
      }
    }

    throw lastErr || new Error("Upload thất bại");
  };

  const hasLocalAsset = (asset) =>
    Boolean(asset && typeof asset === "object" && asset.__localFile instanceof File);

  const isBlob = (url) => typeof url === "string" && url.startsWith("blob:");

  // Auto-create standard S3 folders (best-effort, fires in parallel).
  const createS3Folders = async (slug) => {
    const base = `locations/${slug}/`;
    await Promise.allSettled(
      ["cover", "hero", "content", "videos", "video-thumbnails", "map-icons"].map((f) =>
        apiFetch(`${API}/media/folder`, {
          method: "POST",
          body: JSON.stringify({ prefix: `${base}${f}/` }),
        }).catch(() => {/* best-effort */})
      )
    );
  };

  /**
   * Upload ALL local (blob) assets to S3 at Save time.
   * Existing S3 assets (real keys, no __localFile) pass through unchanged.
   * Returns { cover, videos, heroGallery, mediaBlocks, mapIconAsset }.
   */
  const uploadAllLocalMedia = async (slug, mediaIn, formLike, progressOpts) => {
    const base = `locations/${slug}/`;
    const token = getAdminSessionToken();
    const setProgressNote = (msg) => {
      if (typeof progressOpts?.onUploadNote === "function") progressOpts.onUploadNote(msg);
      else setSaveNote(msg);
    };

    const upSmall = (file, folder) => uploadOneToPrefix(file, `${base}${folder}/`);
    const up = async (file, folder) => {
      const size = Number(file?.size || 0);
      // Multipart above DIRECT_UPLOAD_MAX_BYTES (avoid Lambda Function URL ~6MB / proxy limits).
      if (size > DIRECT_UPLOAD_MAX_BYTES) {
        // Key duy nhất mỗi lần upload (tên-slug + hậu tố) → không ghi đè tệp trùng tên; dùng key/url BE trả về
        const key = buildUniqueUploadKey(`${base}${folder}/`, file, file?.type || "");
        return await multipartUploadToS3({
          apiBase: API,
          token,
          key,
          file,
          contentType: file?.type || "application/octet-stream",
          onProgress: ({ partNumber, partCount }) => {
            setProgressNote(`Đang upload... (${partNumber}/${partCount})`);
          },
        });
      }
      return upSmall(file, folder);
    };
    const toAsset = (out, file) => ({
      key: out.key,
      url: out.url,
      mimeType: out.mimeType || file.type || "",
      size: out.size || file.size || 0,
      alt: "",
    });

    // Cover
    let cover = mediaIn?.cover || null;
    if (hasLocalAsset(cover)) {
      const out = await up(cover.__localFile, "cover");
      cover = toAsset(out, cover.__localFile);
    } else if (cover && isBlob(cover.url)) {
      cover = null;
    }

    // Videos
    const videos = [];
    for (const v of mediaIn?.videos || []) {
      if (!v) continue;
      if (isBlob(v.url) && !hasLocalAsset(v)) continue; // stale blob
      let nextV = { ...v };
      if (hasLocalAsset(nextV)) {
        const out = await up(nextV.__localFile, "videos");
        nextV = { ...toAsset(out, nextV.__localFile), title: nextV.title || "", thumbnail: nextV.thumbnail || {} };
      }
      let thumb = nextV.thumbnail || {};
      if (hasLocalAsset(thumb)) {
        const out = await up(thumb.__localFile, "video-thumbnails");
        thumb = toAsset(out, thumb.__localFile);
      } else if (isBlob(thumb.url)) {
        thumb = {};
      }
      const { __localFile: _f, ...cleanV } = nextV;
      videos.push({ ...cleanV, thumbnail: thumb });
    }

    // Hero gallery
    const heroGallery = [];
    for (const a of formLike.heroGallery || []) {
      if (!a) continue;
      if (hasLocalAsset(a)) {
        heroGallery.push(toAsset(await up(a.__localFile, "hero"), a.__localFile));
      } else if (!isBlob(a.url)) {
        heroGallery.push(a);
      }
    }

    // Content (mediaBlocks)
    const mediaBlocks = [];
    for (const b of formLike.mediaBlocks || []) {
      if (!b) continue;
      if (b.image && hasLocalAsset(b.image)) {
        mediaBlocks.push({ ...b, image: toAsset(await up(b.image.__localFile, "content"), b.image.__localFile) });
      } else if (b.image && isBlob(b.image.url)) {
        mediaBlocks.push({ ...b, image: null });
      } else {
        mediaBlocks.push(b);
      }
    }

    // Map-icon asset
    let mapIconAsset = formLike.mapIconAsset;
    if (hasLocalAsset(mapIconAsset)) {
      mapIconAsset = toAsset(await up(mapIconAsset.__localFile, "map-icons"), mapIconAsset.__localFile);
    }

    return { cover, videos, heroGallery, mediaBlocks, mapIconAsset };
  };

  // Legacy draft/moveKey flow removed: this admin stores files locally and uploads only on Save.

  function remapBlocksWithMovedKeys(blocks, remap) {
    if (!Array.isArray(blocks) || !remap) return Array.isArray(blocks) ? blocks : [];
    return blocks.map((b) => {
      if (!b || typeof b !== "object") return b;
      const out = { ...b };
      if (out.image?.key && remap.has(out.image.key)) out.image = remap.get(out.image.key);
      if (out.thumbnail?.key && remap.has(out.thumbnail.key)) out.thumbnail = remap.get(out.thumbnail.key);
      if (out.video?.key && remap.has(out.video.key)) out.video = { ...out.video, ...remap.get(out.video.key) };
      if (out.video?.thumbnail?.key && remap.has(out.video.thumbnail.key)) {
        out.video = { ...out.video, thumbnail: remap.get(out.video.thumbnail.key) };
      }
      return out;
    });
  }

  const stripLocalFromBlocks = (blocks) =>
    (Array.isArray(blocks) ? blocks : []).map((b) => {
      if (!b || typeof b !== "object") return b;
      if (b.image && hasLocalAsset(b.image)) return { ...b, image: null };
      return b;
    });

  const stripLocalFromHero = (hero) =>
    (Array.isArray(hero) ? hero : []).filter((a) => a && !hasLocalAsset(a));

  const buildPayloadFrom = (formLike, media) => {
    const movedBlocks = mergePreservedMediaBlocks(
      remapBlocksWithMovedKeys(formLike.mediaBlocks || [], media?.remap),
      formLike.preservedMediaBlocks
    );
    const movedHeroGallery = (formLike.heroGallery || [])
      .map((a) => (a?.key && media?.remap?.has(a.key) ? media.remap.get(a.key) : a))
      .filter(Boolean);

      const parsedPrice = parsePriceRange(formLike.priceRange || "");

      const finalPriceMin =
        Number(formLike.priceMin || 0) ||
        parsedPrice.priceMin ||
        Number(onlyDigits(formLike.price || "")) ||
        0;

      const finalPriceMax =
        Number(formLike.priceMax || 0) ||
        parsedPrice.priceMax ||
        0;

      const autoTikTokEmbed = (() => {
      const rawEmbed = String(formLike.tiktokEmbed || "").trim();
      if (rawEmbed) return rawEmbed;
      const rawUrl = String(formLike.tiktokUrl || "").trim();
      const m = rawUrl.match(/\/video\/(\d{10,})/);
      return m?.[1] ? `https://www.tiktok.com/embed/v2/${m[1]}` : "";
    })();

    return {
    name: formLike.name,
    slug: formLike.slug,
    category: String(formLike.category || "").trim() ? formLike.category : undefined,
    categorySlug: String(formLike.categorySlug || "").trim(),
    ward: formLike.ward,
    district: formLike.district,
    city: formLike.city,
    phone: formLike.phone,
    shortIntro: formLike.shortIntro,
    description: formLike.description,
    image: media.cover || { key: "", url: "", mimeType: "", size: 0, alt: "" },
    tiktokEmbed: autoTikTokEmbed,
    tiktokUrl: formLike.tiktokUrl,
    price: finalPriceMin,
    priceMin: finalPriceMin,
    priceMax: finalPriceMax,
    latitude: parseCoordinateInput(formLike.latitude),
    longitude: parseCoordinateInput(formLike.longitude),
    gallery: media.gallery || [],
    heroGallery: movedHeroGallery,
    videos: media.videos || [],
    mediaBlocks: movedBlocks,
    seoTitle: formLike.seoTitle,
    seoDescription: formLike.seoDescription,
    seoKeywords: formLike.seoKeywords
      ? formLike.seoKeywords.split(",").map((s) => s.trim()).filter(Boolean)
      : [],
    highlights: formLike.highlights
      ? formLike.highlights.split("\n").map((s) => s.trim()).filter(Boolean)
      : [],
    tips: formLike.tips
      ? formLike.tips.split("\n").map((s) => s.trim()).filter(Boolean)
      : [],
    faq: formLike.faq
      ? formLike.faq
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [question, ...rest] = line.split("|");
            return {
              question: (question || "").trim(),
              answer: rest.join("|").trim(),
            };
          })
      : [],
    mapIcon: formLike.mapIcon,
    mapIconAsset: formLike.mapIconAsset || { key: "", url: "", mimeType: "", size: 0, alt: "" },
    status: formLike.status,
    geoapifyPlaceId: formLike.geoapifyPlaceId || "",
    openTime: formLike.openTime,
    closeTime: formLike.closeTime,
    homeLinkUrl: formLike.homeLinkUrl,
    homeLinkLabel: formLike.homeLinkLabel,
    };
  };

  const buildPayload = (media) => buildPayloadFrom(form, media);

  // Tab chứa từng ô lỗi → tự chuyển tới tab có lỗi đầu tiên
  const FIELD_TAB = { latitude: "location", longitude: "location", image: "media", gallery: "media", videos: "media" };
  const showErrors = (errors, message) => {
    const keys = Object.keys(errors || {}).filter((k) => errors[k]);
    if (!keys.length) return false;
    setFormErrors(errors);
    setTab(FIELD_TAB[keys[0]] || "basic");
    toast(message || errors[keys[0]], "error");
    return true;
  };
  /** Lỗi từ backend (422): gắn vào đúng ô nhập. Trả true nếu đã xử lý. */
  const applyServerError = (err) => {
    if (err?.code === "VALIDATION_ERROR" && err.details) return showErrors(err.details, err.message);
    if (err?.code === "PUBLISH_REQUIREMENTS") {
      const errs = Object.fromEntries((err.details?.blockers || []).map((b) => [b.field, b.message]));
      showErrors(errs, err.message);
      return true;
    }
    return false;
  };

  const handleSave = async (e, overrideStatus) => {
    e?.preventDefault?.();
    setFormErrors({});

    // Kiểm tra nhanh phía trình duyệt (backend vẫn kiểm tra lại)
    const clientErrors = {};
    if (!String(form.name || "").trim()) clientErrors.name = "Vui lòng nhập tên địa điểm";
    else if (String(form.name).trim().length < 2) clientErrors.name = "Tên quá ngắn (tối thiểu 2 ký tự)";
    if (!String(form.category || "").trim()) clientErrors.category = "Vui lòng chọn danh mục";
    const timeRe = /^([01]?\d|2[0-3]):[0-5]\d$/;
    if (form.openTime && !timeRe.test(String(form.openTime))) clientErrors.openTime = "Giờ phải theo dạng HH:mm (vd 07:30)";
    if (form.closeTime && !timeRe.test(String(form.closeTime))) clientErrors.closeTime = "Giờ phải theo dạng HH:mm (vd 07:30)";
    if (form.phone && !/^[0-9+().\-\s]{6,20}$/.test(String(form.phone))) clientErrors.phone = "Số điện thoại không hợp lệ";
    if (String(form.shortIntro || "").length > 300) clientErrors.shortIntro = "Giới thiệu ngắn tối đa 300 ký tự";

    const lat = parseCoordinateInput(form.latitude);
    const lng = parseCoordinateInput(form.longitude);
    if (!Number.isFinite(lat)) clientErrors.latitude = "Vĩ độ không hợp lệ (vd 11,9404)";
    else if (lat < -90 || lat > 90) clientErrors.latitude = "Vĩ độ phải trong khoảng -90 đến 90";
    if (!Number.isFinite(lng)) clientErrors.longitude = "Kinh độ không hợp lệ (vd 108,4583)";
    else if (lng < -180 || lng > 180) clientErrors.longitude = "Kinh độ phải trong khoảng -180 đến 180";

    if (showErrors(clientErrors, "Còn thông tin chưa hợp lệ — các ô được đánh dấu màu đỏ cần sửa.")) return;

    const formSnapshot = { ...form, ...(overrideStatus ? { status: overrideStatus } : {}), latitude: lat, longitude: lng };
    setForm((p) => ({ ...p, ...(overrideStatus ? { status: overrideStatus } : {}), latitude: lat, longitude: lng }));
    // Nháp cục bộ chỉ xoá khi lưu THÀNH CÔNG (lỗi giữa chừng vẫn khôi phục được)

    const draftSnap = {
      cover: draftMedia.cover,
      gallery: Array.isArray(draftMedia.gallery) ? [...draftMedia.gallery] : [],
      videos: Array.isArray(draftMedia.videos) ? [...draftMedia.videos] : [],
    };

    if (modalMode === "add") {
      const targetStatus = formSnapshot.status || "draft";
      const label = String(formSnapshot.name || "").trim() || "địa điểm mới";
      // Cùng 1 khoá cho mọi lần thử của lần gửi này (và cho lần gửi lại nếu kết quả trước chưa rõ) → BE không tạo trùng
      const idemKey = addIdemKeyRef.current || makeIdempotencyKey();
      addIdemKeyRef.current = null;

      // Tiến trình nền chỉ dùng snapshot. Form dùng chung trả về trống ngay để người dùng mở địa điểm khác
      // mà không bị tiến trình này ghi đè; nháp cục bộ "new" giữ tới khi thành công.
      writeLocalDraft("new", formSnapshot);
      setModalOpen(false);
      modalOpenRef.current = false;
      setSaving(false);
      setSaveNote("");
      setFormErrors({});
      setForm(createEmptyForm());
      setDraftMedia(createEmptyDraftMedia());
      setVideoDraft(createEmptyVideoDraft());
      setTab("basic");

      const jobCtl = beginJob({
        kind: "location-add",
        title: `Thêm địa điểm: ${String(formSnapshot.name || "").trim() || "—"}`,
        stepLabels: [
          "Tạo bản ghi địa điểm (nháp)",
          "Chuẩn bị kho lưu trữ (S3)",
          "Upload ảnh & video",
          "Lưu thông tin cuối",
          ...(targetStatus !== "draft" ? [targetStatus === "published" ? "Đăng địa điểm" : "Gửi duyệt"] : []),
        ],
      });

      const retryOpts = { retries: 2, baseDelayMs: 650, maxDelayMs: 5500 };

      (async () => {
        let createdId = null;
        try {
          jobCtl.setActiveStep(0);
          jobCtl.setSubtitle("");
          // Luôn tạo ở trạng thái Nháp: điều kiện đăng (ảnh đại diện...) chỉ đủ sau khi upload media
          const initialPayload = buildPayloadFrom(
            {
              ...formSnapshot,
              status: "draft",
              heroGallery: [],
              mediaBlocks: [],
              preservedMediaBlocks: [],
              mapIconAsset: createEmptyForm().mapIconAsset,
            },
            { cover: null, gallery: [], videos: [], remap: new Map() }
          );
          const postBody = JSON.stringify(initialPayload);

          let res = null;
          for (let attempt = 0; ; attempt++) {
            try {
              res = await apiFetch(`${API}/locations`, {
                method: "POST",
                body: postBody,
                headers: { "X-Idempotency-Key": idemKey },
                retry: { ...retryOpts, allowNonIdempotent: true },
              });
              break;
            } catch (err) {
              // 409 "đang xử lý" (yêu cầu cùng khoá còn chạy) → chờ rồi gửi lại cùng khoá để nhận đúng kết quả
              if (isAddInProgressConflict(err) && attempt < 3) {
                jobCtl.setSubtitle("Yêu cầu trước đang được xử lý — đang chờ…");
                await sleep(1500 * (attempt + 1));
                continue;
              }
              throw err;
            }
          }

          const created = res?.data || res;
          createdId = created?._id || null;
          if (!createdId) {
            const err = new Error("Máy chủ không trả về mã địa điểm vừa tạo. Hãy tải lại danh sách để kiểm tra trước khi thử lại.");
            err.uncertain = true;
            throw err;
          }
          // Đã có bản ghi: từ đây mọi khôi phục là "chỉnh sửa" địa điểm này (không POST lại)
          removeLocalDraft("new");
          writeLocalDraft(createdId, { ...formSnapshot, status: "draft" });

          const finalSlug = safeFolderSlug(created?.slug || formSnapshot.slug || formSnapshot.name, "location");

          jobCtl.setActiveStep(1);
          jobCtl.setSubtitle("");
          await createS3Folders(finalSlug);

          jobCtl.setActiveStep(2);
          const uploaded = await uploadAllLocalMedia(finalSlug, draftSnap, formSnapshot, {
            onUploadNote: (m) => jobCtl.setSubtitle(m),
          });

          jobCtl.setActiveStep(3);
          jobCtl.setSubtitle("");
          const finalPayload = buildPayloadFrom(
            {
              ...formSnapshot,
              status: "draft",
              heroGallery: uploaded.heroGallery,
              mediaBlocks: uploaded.mediaBlocks,
              mapIconAsset: uploaded.mapIconAsset,
            },
            {
              cover: uploaded.cover,
              gallery: draftSnap.gallery,
              videos: uploaded.videos,
              remap: new Map(),
            }
          );
          const putRes = await apiFetch(`${API}/locations/${createdId}`, {
            method: "PUT",
            body: JSON.stringify(finalPayload),
            retry: retryOpts,
          });
          let saved = putRes?.data || putRes || created;

          // Chuyển sang trạng thái người dùng chọn (Đăng / Gửi duyệt) sau khi đã đủ media
          let statusErr = null;
          if (targetStatus !== "draft") {
            jobCtl.setActiveStep(4);
            try {
              const st = await apiFetch(`${API}/locations/${createdId}/status`, {
                method: "PATCH",
                body: JSON.stringify({ status: targetStatus }),
                retry: retryOpts,
              });
              saved = st?.data || saved;
            } catch (e) {
              statusErr = e;
            }
          }

          removeLocalDraft(createdId);
          jobCtl.complete();
          fetch();

          if (statusErr) {
            const blockers = Array.isArray(statusErr?.details?.blockers) ? statusErr.details.blockers : [];
            const why = blockers.length
              ? blockers.map((b) => b.message).join("; ")
              : String(statusErr?.message || "").trim() || "lỗi không xác định";
            const verb = targetStatus === "published" ? "đăng" : "gửi duyệt";
            toast(`Đã lưu "${label}" dạng nháp nhưng chưa thể ${verb}: ${why}.`, "error", {
              action: { label: "Mở để sửa", onClick: () => openEditIfIdle(saved) },
            });
          } else {
            toast(
              targetStatus === "published"
                ? `Đã thêm và đăng "${label}".`
                : targetStatus === "review"
                  ? `Đã thêm "${label}" và gửi duyệt.`
                  : `Đã lưu nháp "${label}".`,
              "success"
            );
          }
        } catch (err) {
          jobCtl.fail(err);
          if (createdId) fetch();
          // Chưa tạo được & kết quả chưa rõ (mạng / 5xx / 409 đang xử lý) → giữ khoá để lần gửi lại không tạo trùng
          const uncertain =
            !createdId &&
            Boolean(err?.uncertain || err?.status == null || Number(err.status) >= 500 || isAddInProgressConflict(err));
          const restoreArgs = { snapshot: formSnapshot, media: draftSnap, createdId, idemKey: uncertain ? idemKey : null };
          const msg = String(err?.message || "").trim() || "Không lưu được. Bạn có thể chỉnh sửa và thử lại.";
          const prefix = createdId
            ? `Đã tạo "${label}" (nháp) nhưng chưa lưu xong ảnh/thông tin`
            : `Không thêm được "${label}"`;

          if (!modalOpenRef.current) {
            restoreFailedAdd(restoreArgs);
            if (!applyServerError(err)) toast(`${prefix}. ${msg}`, "error", { error: err });
          } else {
            // Đang mở form khác → không ghi đè; cho phép mở lại dữ liệu khi sẵn sàng
            toast(`${prefix}. ${msg}`, "error", {
              error: err,
              action: {
                label: "Mở lại",
                onClick: () => {
                  if (restoreFailedAdd(restoreArgs)) applyServerError(err);
                },
              },
            });
          }
        }
      })();

      return;
    }

    setSaving(true);
    setSaveNote("");

    try {
      const retryOpts = (verb) => ({
        retries: 5,
        baseDelayMs: 650,
        maxDelayMs: 5500,
        ...(verb === "POST" ? { allowNonIdempotent: true } : {}),
      });

      const finalSlug = safeFolderSlug(formSnapshot.slug || formSnapshot.name, "location");

      // Upload all local blobs then send final payload
      const uploaded = await uploadAllLocalMedia(finalSlug, draftMedia, formSnapshot);
      const finalPayload = buildPayloadFrom(
        {
          ...formSnapshot,
          heroGallery: uploaded.heroGallery,
          mediaBlocks: uploaded.mediaBlocks,
          mapIconAsset: uploaded.mapIconAsset,
        },
        { cover: uploaded.cover || draftMedia.cover, gallery: draftMedia.gallery, videos: uploaded.videos, remap: new Map() }
      );

      await apiFetch(`${API}/locations/${editId}`, {
        method: "PUT",
        body: JSON.stringify(finalPayload),
        retry: retryOpts("PUT"),
      });

      clearLocalDraft();
      toast("Gửi yêu cầu cập nhật thành công.", "success");

      setModalOpen(false);
      fetch();
    } catch (e) {
      if (!applyServerError(e)) {
        const msg = String(e?.message || "").trim() || "Bạn có thể thử lại.";
        toast(`Chưa lưu được thay đổi. ${msg}`, "error", { error: e });
      }
    } finally {
      setSaving(false);
      setSaveNote("");
    }
  };

  // Store cover as local blob — no S3 upload until Save.
  const uploadCoverDraft = (file) => {
    if (!file) return;
    setDraftMedia((p) => ({
      ...p,
      cover: { __localFile: file, url: URL.createObjectURL(file), mimeType: file.type || "", size: file.size || 0, key: "", alt: "" },
    }));
  };

  // Store videos as local blobs — no S3 upload until Save.
  // If thumbnail not provided, auto-generate a frame thumbnail for preview.
  const uploadVideosDraft = async (videoFiles, thumbFiles, titlesStr) => {
    const vfs = Array.from(videoFiles || []).slice(0, 10);
    if (!vfs.length) return;

    const tfs = Array.from(thumbFiles || []);
    const titles = String(titlesStr || "").split("\n").map((s) => s.trim()).filter(Boolean);

    setUploading((p) => ({ ...p, videos: true }));
    try {
      const added = [];
      for (let i = 0; i < vfs.length; i++) {
        const vf = vfs[i];
        const tf = tfs[i];
        const videoUrl = URL.createObjectURL(vf);

        let thumbFile = tf || null;
        if (!thumbFile) {
          try {
            thumbFile = await createVideoThumbFile(vf);
          } catch {
            thumbFile = null;
          }
        }

        const thumbAsset = thumbFile
          ? {
              __localFile: thumbFile,
              url: URL.createObjectURL(thumbFile),
              mimeType: thumbFile.type || "",
              size: thumbFile.size || 0,
              key: "",
              alt: "",
            }
          : {};

        added.push({
          __localFile: vf,
          url: videoUrl,
          mimeType: vf.type || "",
          size: vf.size || 0,
          key: "",
          title: titles[i] || vf.name.replace(/\.[^.]+$/, ""),
          thumbnail: thumbAsset,
        });
      }

      setDraftMedia((p) => ({ ...p, videos: [...p.videos, ...added] }));
      setVideoDraft(createEmptyVideoDraft());
      toast(`Đã thêm ${added.length} video — sẽ upload khi bấm Lưu thay đổi`, "info");
    } finally {
      setUploading((p) => ({ ...p, videos: false }));
    }
  };

  const dirty = modalOpen && baselineRef.current !== "" && sig(form, draftMedia) !== baselineRef.current;
  const validation = useMemo(() => validateLocationForm(form, draftMedia), [form, draftMedia]);
  const alreadyPublished = modalMode === "edit" && (form.status === "published");
  const publishAs = can("locations:publish") ? "published" : can("locations:write") ? "review" : "save";

  // Mốc "đã lưu": đặt khi mở modal. Thêm mới mà form đang có nội dung (mở lại sau khi đóng nhầm) → so với form trống.
  useEffect(() => {
    if (!modalOpen) {
      baselineRef.current = "";
      setDraftBanner(null);
      return;
    }
    baselineRef.current = modalMode === "add" ? sig(createEmptyForm(), createEmptyDraftMedia()) : sig(form, draftMedia);
    const saved = readDraft();
    if (saved?.form && JSON.stringify(saved.form) !== JSON.stringify(form)) {
      setDraftBanner({ savedAt: saved.savedAt });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, editId, modalMode]);

  // Tự lưu nháp cục bộ (mất cũng không sao khi refresh): chỉ dữ liệu văn bản, ảnh chưa tải lên không được lưu
  useEffect(() => {
    if (!dirty || saving) return undefined;
    const t = setTimeout(() => {
      try {
        const savedAt = Date.now();
        localStorage.setItem(draftKey, JSON.stringify({ savedAt, form }));
        setLastAutosave(savedAt);
      } catch {
        /* hết dung lượng / chế độ riêng tư */
      }
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dirty, saving]);

  // Cảnh báo khi đóng tab/refresh lúc còn thay đổi chưa lưu
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const requestClose = () => {
    if (dirty && !saving) setGuardOpen(true);
    else setModalOpen(false);
  };

  return (
    <div className="space-y-6 pb-10">
      <LocationList
        categories={categories}
        reloadKey={reloadKey}
        onAdd={openAdd}
        onEdit={openEdit}
        addDisabled={hasRunningJob}
      />

      <LocationEditorModal
        open={modalOpen}
        onClose={requestClose}
        title={modalMode === "add" ? "Thêm địa điểm mới" : "Chỉnh sửa địa điểm"}
        TABS={TABS}
        tab={tab}
        setTab={setTab}
        form={form}
        setForm={setForm}
        categories={categories}
        saving={saving}
        saveNote={saveNote}
        modalMode={modalMode}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        goPrevStep={goPrevStep}
        goNextStep={goNextStep}
        handleSave={handleSave}
        draftMedia={draftMedia}
        setDraftMedia={setDraftMedia}
        uploading={uploading}
        uploadCoverDraft={uploadCoverDraft}
        uploadVideosDraft={uploadVideosDraft}
        videoDraft={videoDraft}
        setVideoDraft={setVideoDraft}
        MapPicker={MapPicker}
        round6={round6}
        toast={toast}
        formErrors={formErrors}
        onClearError={clearFormError}
        onSetErrors={setFormErrors}
        validation={validation}
        dirty={dirty}
        lastAutosave={lastAutosave}
        draftBanner={
          draftBanner
            ? {
                savedAt: draftBanner.savedAt,
                onRestore: () => {
                  const saved = readDraft();
                  if (saved?.form) setForm({ ...createEmptyForm(), ...saved.form });
                  setDraftBanner(null);
                },
                onDiscard: clearLocalDraft,
              }
            : null
        }
        onSubmit={(status) => handleSave(null, status)}
        publishAs={publishAs}
        alreadyPublished={alreadyPublished}
      />

      {/* Đóng khi còn thay đổi chưa lưu — portal cùng chỗ với LocationEditorModal và z cao hơn, nếu không sẽ bị modal che */}
      {guardOpen ? createPortal(
        <DialogModal
          open
          onClose={() => setGuardOpen(false)}
          maxWidthClass="max-w-md"
          zClass="z-[220]"
          title="Bạn có thay đổi chưa lưu"
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <DialogButton variant="secondary" onClick={() => setGuardOpen(false)} autoFocus>
                Tiếp tục chỉnh sửa
              </DialogButton>
              <DialogButton
                variant="danger"
                onClick={() => {
                  setGuardOpen(false);
                  clearLocalDraft();
                  setForm(createEmptyForm());
                  setDraftMedia(createEmptyDraftMedia());
                  setVideoDraft(createEmptyVideoDraft());
                  setModalOpen(false);
                }}
              >
                Thoát, bỏ thay đổi
              </DialogButton>
              {!alreadyPublished ? (
                <DialogButton
                  variant="primary"
                  onClick={() => {
                    setGuardOpen(false);
                    handleSave(null, "draft");
                  }}
                >
                  Lưu nháp rồi thoát
                </DialogButton>
              ) : null}
            </div>
          }
        >
          <p className="text-sm text-admin-text">
            Nếu thoát bây giờ, các thay đổi trong <b>{form.name?.trim() || "địa điểm này"}</b> sẽ không được lưu lên hệ thống.
          </p>
          <p className="mt-2 text-xs text-admin-textMuted">Bản nháp tự lưu trên trình duyệt này vẫn được giữ — bạn có thể khôi phục khi mở lại.</p>
        </DialogModal>,
        document.querySelector(".admin-root") || document.body
      ) : null}

      <ToastPortal />
    </div>
  );
};

export default AdminLocations;
